import React from "react";
import { useTranslation } from "react-i18next";
import { pocketType } from "../../data/types";

import Moment from "react-moment";
import CurrencyFormat from "react-currency-format";

import "./bill.css";

/**
 * Shared bill layout used by the print and download modals.
 * Left: worker cutting slips (pant / shirt) — measurement grid
 * positions are fixed, the tailor reads them by position.
 * Right: customer invoice.
 */
const BillContent = ({ order, billId, innerRef }) => {
  const { t } = useTranslation();

  const splitTags = (options, ns) =>
    options
      ? options
          .split(",")
          .filter(Boolean)
          .map((item) => t(`${ns}.options.${item}`))
      : [];

  const getPocketType = (type) => {
    const result = pocketType.find((item) => item.key === type);
    return result ? result.name : type;
  };

  const money = (value) => (
    <CurrencyFormat
      value={value || 0}
      displayType={"text"}
      thousandSeparator={true}
      prefix={"₹"}
    />
  );

  const grandTotal = (clothAdvance, advance, amount) => {
    let total = 0;
    if (clothAdvance) total += clothAdvance;
    if (advance) total -= advance;
    if (amount) total += amount;
    return total;
  };

  if (!order) return null;

  const { customer } = order;

  /**
   * A garment can exist as an all-but-empty row (count 0, no measurements) when
   * it was left unticked on an older order. Those carry nothing for the tailor,
   * so treat them as absent rather than printing a blank cutting slip.
   */
  const hasContent = (garment, countKey, measureKeys) => {
    if (!garment) return false;
    if (Number(garment[countKey]) > 0) return true;
    if (measureKeys.some((k) => String(garment[k] ?? "").trim() !== "")) return true;
    return String(garment.pantDetails ?? garment.shirtDetails ?? "").trim() !== "";
  };

  const pantKeys = ["p1","p2","p3","p4","p5","p6","p7","p8","p9","p10"];
  const shirtKeys = ["s1","s2","s3","s4","s5","s6","s7","s8","s9","s10","s11","s12","s13"];

  const pant = hasContent(customer.pant, "pantCount", pantKeys) ? customer.pant : null;
  const shirt = hasContent(customer.shirt, "shirtCount", shirtKeys) ? customer.shirt : null;

  return (
    <div className="bill-sheet" id={billId} ref={innerRef}>
      {/* ---------- worker cutting slips ---------- */}
      <div className="bill-left">
        {pant && (
          <div className="slip">
            <div className="slip-head">
              <span className="no">{order.billNo}</span>
              <span className="nm">{customer.name}</span>
              <span className="dt">
                <Moment subtract={{ days: 1 }} format="DD/MM/YYYY">
                  {order.deliveryDate}
                </Moment>
              </span>
            </div>
            <div className="slip-sub">
              <span>
                {t("common.pant")} · {pant.pantCount ? pant.pantCount : 0}
              </span>
              <span className="ty">{t(`pant.type.${pant.pantType}`)}</span>
            </div>
            <div className="slip-nums pant">
              <span>{pant.p1}</span>
              <span>{pant.p2}</span>
              <span>{pant.p3}</span>
              <span>{pant.p4}</span>
              <span>{pant.p5}</span>
              <span>{pant.p6}</span>
              <span>{pant.p7}</span>
              <span></span>
              <span>{pant.p8}</span>
              <span>{pant.p9}</span>
              <span></span>
              <span></span>
              <span>{pant.p10}</span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="slip-foot">
              <div className="slip-foot-img">
                <img
                  src={`./images/pant-images/${pant.image}.png`}
                  alt="pant style"
                />
              </div>
              <div className="slip-foot-body">
                <div className="slip-tags">
                  {pant.pocketType && (
                    <span className="slip-tag">
                      {getPocketType(pant.pocketType)}
                    </span>
                  )}
                  {splitTags(pant.pantOptions, "pant").map((opt, i) => (
                    <span className="slip-tag" key={i}>
                      {opt}
                    </span>
                  ))}
                </div>
                {pant.pantDetails && (
                  <div className="slip-details">
                    <strong>{t("common.details")} :</strong> {pant.pantDetails}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {shirt && (
          <div className="slip">
            <div className="slip-head">
              <span className="no">{order.billNo}</span>
              <span className="nm">{customer.name}</span>
              <span className="dt">
                <Moment subtract={{ days: 1 }} format="DD/MM/YYYY">
                  {order.deliveryDate}
                </Moment>
              </span>
            </div>
            <div className="slip-sub">
              <span>
                {t("common.shirt")} · {shirt.shirtCount ? shirt.shirtCount : 0}
              </span>
              <span className="ty">{t(`shirt.type.${shirt.shirtType}`)}</span>
            </div>
            <div className="slip-nums shirt">
              <span>{shirt.s1}</span>
              <span>{shirt.s2}</span>
              <span>{shirt.s3}</span>
              <span>{shirt.s4}</span>
              <span>{shirt.s5}</span>
              <span></span>
              <span>{shirt.s6}</span>
              <span></span>
              <span>{shirt.s7}</span>
              <span></span>
              <span>{shirt.s8}</span>
              <span>{shirt.s9}</span>
              <span>{shirt.s10}</span>
              <span>{shirt.s11}</span>
              <span></span>
              <span>{shirt.s12}</span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span>{shirt.s13}</span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="slip-foot">
              <div className="slip-foot-img">
                <img
                  src={`./images/shirt-images/${shirt.image}.png`}
                  alt="shirt style"
                />
              </div>
              <div className="slip-foot-body">
                <div className="slip-tags">
                  {splitTags(shirt.shirtOptions, "shirt").map((opt, i) => (
                    <span className="slip-tag" key={i}>
                      {opt}
                    </span>
                  ))}
                </div>
                {shirt.shirtDetails && (
                  <div className="slip-details">
                    <strong>{t("common.details")} :</strong> {shirt.shirtDetails}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ---------- customer invoice ---------- */}
      <div className="bill-right">
        <div className="invoice">
          <div className="inv-head">
            <div className="stamp">
              DARJI
              <br />
              POINT
            </div>
            <div className="n gu">દરજી પોઇન્ટ</div>
            <div className="en">Selection &amp; Tailors</div>
            <div className="tag gu">પેન્ટ · શર્ટ · શૂટ · સફારી · શેરવાની</div>
            <div className="addr gu">
              G-18, વ્રજ એન્ક્લેવ, લલિતા ચોકડી પાસે,  આંબતલાવડી રોડ, કતારગામ, સુરત
            </div>
            <div className="phone">📞 73591 88376</div>
          </div>

          <div className="inv-body">
            <div className="inv-meta">
              <div>
                <div className="k">{t("common.name")}</div>
                <div className="v gu">{customer.name}</div>
              </div>
              <div>
                <div className="k">{t("common.billNo")}</div>
                <div className="v mono">{order.billNo}</div>
              </div>
              <div>
                <div className="k">{t("common.mobile")}</div>
                <div className="v mono">{customer.mobile}</div>
              </div>
              <div>
                <div className="k">{t("common.date")}</div>
                <div className="v mono">
                  <Moment format="DD/MM/YYYY">{order.date}</Moment>
                </div>
              </div>
              <div>
                <div className="k">{t("common.address")}</div>
                <div className="v gu">{customer.address}</div>
              </div>
              <div>
                <div className="k">{t("common.delieveryDate")}</div>
                <div className="v mono">
                  <Moment format="DD/MM/YYYY">{order.deliveryDate}</Moment>
                </div>
              </div>
            </div>

            <table className="inv-tbl">
              <thead>
                <tr>
                  <th style={{ width: "58%" }}>{t("common.details")}</th>
                  <th style={{ width: "16%" }}>{t("common.qty")}</th>
                  <th style={{ width: "26%" }}>{t("common.total")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="it">
                    {pant && pant.pantCount
                      ? t(`pant.type.${pant.pantType}`)
                      : ""}
                  </td>
                  <td className="mono">
                    {pant && pant.pantCount ? pant.pantCount : ""}
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td className="it">
                    {shirt && shirt.shirtCount
                      ? t(`shirt.type.${shirt.shirtType}`)
                      : ""}
                  </td>
                  <td className="mono">
                    {shirt && shirt.shirtCount ? shirt.shirtCount : ""}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <div className="inv-tot">
              <div className="r">
                <span className="k">{t("common.clothAdvance")}</span>
                <span className="v">{money(order.clothAdvance)}</span>
              </div>
              <div className="r">
                <span className="k">{t("common.advance")}</span>
                <span className="v">
                  {order.advance ? <>&minus; {money(order.advance)}</> : money(0)}
                </span>
              </div>
              <div className="r">
                <span className="k">{t("common.amount")}</span>
                <span className="v">{money(order.amount)}</span>
              </div>
              <div className="r grand">
                <span className="k">{t("common.total")}</span>
                <span className="v">
                  {money(
                    grandTotal(order.clothAdvance, order.advance, order.amount),
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="inv-foot gu">આભાર · ફરી પધારજો</div>
        </div>
      </div>
    </div>
  );
};

export default BillContent;
